import { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { ISCOArchetypeContext } from '@providers/ISCOArchetype';
import { CountriesContext } from '@providers/Countries';

import * as url from '@api/requests';
import { Timelines } from '@config/timelines';

import * as am5 from '@amcharts/amcharts5';
import * as am5hierarchy from '@amcharts/amcharts5/hierarchy';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';
import ChildISCOChart from './ChildISCOChart';

import './ParentISCOChart.scss';

import { DefaultAvatarsClassification } from '@config/avatars-classification';
import LoaderImage from '@assets/loader.gif';

type ParentISCOChartProps = {
    countryCode: string;
}

const ParentISCOChart = ({ countryCode }: ParentISCOChartProps) => {
    const { setSelectedParentISCO, selectedParentISCO, setSelectedChildISCO, showISCODetails, showSkillsDetails } = useContext(ISCOArchetypeContext);
    const { countryDetails } = useContext(CountriesContext);
    const [data, setData] = useState<any>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const chartId = "parent-isco-chart" + Date.now().toString().substr(-4);

    const rootRef = useRef<am5.Root | null>(null);

    const countryData = useMemo(() => {
        return (
            countryDetails?.find(({ code }: any) => code.toLowerCase() === countryCode) || null
        );
    }, [countryCode, countryDetails]);


    useEffect(() => {
        const fetchData = async () => {
            if (countryData && countryData.hubId) {
                setData(null);
                setSelectedParentISCO && setSelectedParentISCO(null);
                setSelectedChildISCO && setSelectedChildISCO(null);
                const currentTimeline = Timelines.find(t => t.hubId === countryData.hubId);
                if (currentTimeline) {
                    // Get last year data
                    const fromTimeline = currentTimeline.timelines[currentTimeline.timelines.length - 4];
                    const toTimeline = currentTimeline.timelines[currentTimeline.timelines.length - 1];

                    setIsLoading(true);

                    const [errRes, incomingRes] = await url.getTopIsco3Isco4ByCountry([countryData.hubId], fromTimeline, toTimeline);
                    if (errRes) {
                        console.warn(errRes);
                    } else {
                        incomingRes && incomingRes.results && setData && setData(incomingRes.results);
                    }

                    setIsLoading(false);
                }
            }
        };
        fetchData();
    }, [countryData, setSelectedParentISCO, setSelectedChildISCO]);

    useLayoutEffect(() => {
        if (!selectedParentISCO && data && data.length > 0) {
            
            am5.addLicense('AM5M326788873');

            // Initialize amChart Root instance
            rootRef.current = am5.Root.new(chartId);

            const root = rootRef.current;

            // Set slice color from the following list
            const defaultTheme = am5.Theme.new(root);
            root.setThemes([am5themes_Animated.new(root), defaultTheme]);

            // Enable zooming and panning on the chart
            root.container.set("interactive", true);
            root.container.set("wheelable", true);

            defaultTheme.rule("ColorSet").set("colors", [
                am5.color(0x003b5c),
                am5.color(0x035684),
                am5.color(0x036eaa),
                am5.color(0x0486cf),
                am5.color(0x00293f),
                am5.color(0x118ab2),
                am5.color(0x253948),
                am5.color(0x385870),
                am5.color(0x487292),
                am5.color(0x06d6a0),
                am5.color(0x5686aa),
                am5.color(0x073b4c),
                am5.color(0x71afde),
            ]);

            var container = root.container.children.push(
                am5.Container.new(root, {
                    width: am5.percent(100),
                    height: am5.percent(100),
                    layout: root.verticalLayout
                })
            );

            const maxRadius = 120; // Set your desired maximum radius
            const minRadius = 30; // Set your minimum radius

            var series = container.children.push(
                am5hierarchy.ForceDirected.new(root, {
                    singleBranchOnly: false,
                    downDepth: 2,
                    topDepth: 1,
                    initialDepth: 1,
                    valueField: "isco3_job_count_modified",
                    categoryField: "isco3_label",
                    childDataField: "children",
                    xField: "x",
                    yField: "y",
                    minRadius: minRadius,
                    maxRadius: maxRadius,
                    interactive: true,
                    manyBodyStrength: -85,
                    centerStrength: 1.2,
                })
            );

            series.labels.template.set("forceHidden", true);



            // Define data
            let modifiedData = {
                value: 0,
                children: data
            };

            // Find the maximum value in the data
            const maxCircleValue = 1200;
            const maxValue = Math.max(...modifiedData.children.map((child: any) => child.isco3_job_count));

            // Modify the data to add Skill and Profile children to ISCO4 level and adjust the value to be proportional on the chart
            if (modifiedData.children) {
                let newISCO3s = modifiedData.children.map((isco3: any) => {
                    let newISCO4s = isco3.childrenISCO4.map((isco4: any) => {
                        isco4.children = [
                            { isco4_label: "Profile", isco4_job_count_modified: 50 },
                            { isco4_label: "Skill", isco4_job_count_modified: 50 }
                        ];
                        // Adjust the job counts to be proportional on the chart as per maxCircleValue
                        isco4.isco4_job_count_modified = maxValue > 0 ? parseInt((maxCircleValue * isco4.isco4_job_count / maxValue).toString()) : 0; 
                        return isco4;
                    });
                    isco3.childrenISCO4 = newISCO4s;
                    isco3.isco3_job_count_modified = maxValue > 0 ? parseInt((maxCircleValue * isco3.isco3_job_count / maxValue).toString()) : 0; 
                    return isco3;
                });
                modifiedData.children = newISCO3s;
            }

            series.nodes.template.setup = function (target: any) {
                let label: any = null; // Declare label variable

                target.events.on("dataitemchanged", function (ev: any) {
                    const dataItem = ev.target.dataItem;
                    const value = dataItem.dataContext.isco3_job_count_modified; // Get the value
                    const radius = minRadius + ((value / maxCircleValue) * (maxRadius - minRadius)); // Calculate the radius

                    // Clear previous shapes and icons if they exist
                    target.children.each(function (child: any) {
                        if (child instanceof am5.Container) {
                            child.dispose(); // Dispose of the previous container holding circle and picture
                        }
                    });

                    // Create a new container for the circle and picture
                    let container = target.children.push(am5.Container.new(root, {}));

                    // Create a circular white background
                    container.children.push(am5.Circle.new(root, {
                        radius: radius,
                        fill: am5.color(0xffffff), // White circle background
                        stroke: dataItem.dataContext.isco3_impact === "Shrinking" ? am5.color(0xf7aba3)
                            : dataItem.dataContext.isco3_impact === "Growing" ? am5.color(0xdbff9e)
                                : dataItem.dataContext.isco3_impact === "Stable" ? am5.color(0xfcd695)
                                    : am5.color(0x043c5b),
                        strokeWidth: 8,
                        centerX: am5.percent(50), // Center the circle
                        centerY: am5.percent(50)  // Center the circle
                    }));

                    // Create and position the background image
                    container.children.push(am5.Picture.new(root, {
                        width: am5.percent(97),       // Make the image fill the container
                        height: am5.percent(97),      // Make the image fill the container
                        centerX: am5.percent(50),      // Center the image
                        centerY: am5.percent(50),      // Center the image
                        src: process.env.REACT_APP_CHAT_ISCO_ARCHETYPE_URL + DefaultAvatarsClassification[countryCode].theme + "/" + DefaultAvatarsClassification[countryCode].region + "/" + DefaultAvatarsClassification[countryCode].ethnicity + "/" + dataItem.dataContext.isco3_code + ".png",
                        scaleMode: "contain"           // Scale the image to fit within the circle
                    }));

                    // Dispose of the old label if it exists
                    if (label) {
                        label.dispose();
                    }

                    // Create the new label and position it below the circle
                    label = am5.Label.new(root, {
                        text: dataItem.dataContext.isco3_label,
                        fill: am5.color(0x043c5b),
                        fontSize: 14,
                        y: radius + 20, // Set vertical space below the circle
                        zIndex: 100, // Set the zIndex to a high value to ensure it's on top
                        oversizedBehavior: "truncate",
                        maxWidth: 250,
                        height: 30,
                        // Add padding to make room for the rounded corners
                        paddingLeft: 10,
                        paddingRight: 10,
                        paddingTop: 7,
                        paddingBottom: 5,
                        background: am5.RoundedRectangle.new(root, {
                            fill: am5.color(0xffffff),
                        })
                    });

                    // Add the label after the container to ensure it's on top
                    target.children.push(label);
                });

                // Add click event listener to the target
                target.events.on("click", function (currentTarget: any) {
                    setSelectedParentISCO && setSelectedParentISCO(currentTarget.target.dataItem.dataContext); // Set the selected item
                });

                // Customize tooltip visibility for the nodes of skill and profile
                target.adapters.add('tooltipText', (value: string | undefined, target: any) => {
                    return target.dataItem.dataContext.isco3_label + ": " + target.dataItem.dataContext.isco3_job_count;
                });
            };

            series.data.setAll([modifiedData]);

            // Enable zooming with mouse wheel on the entire chart
            const handleWheel = (event: any) => {
                event.originalEvent.preventDefault(); // Prevent default scrolling

                // Determine zoom factor
                const zoomFactor = event.originalEvent.deltaY > 0 ? 1.1 : 0.9; // Zoom out or in

                // Get the current scale
                const scale = series.get("scale") || 1;

                // Update scale on the series
                series.set("scale", scale * zoomFactor);
            };

            // Add wheel event listener to the root container
            root.container.events.on("wheel", handleWheel);

            series.set("selectedDataItem", series.dataItems[0]);
            
            // Cleanup on unmount
            return () => {
                root && root.dispose();
                if (rootRef && rootRef.current) {
                    rootRef.current = null;
                }
            };
        }
    }, [selectedParentISCO, setSelectedParentISCO, data]); // eslint-disable-line

    const handleGoBackToParentISCOChart = useCallback(() => {
        setSelectedParentISCO && setSelectedParentISCO(null);
    }, [setSelectedParentISCO]);

    return <>
        {!showISCODetails && !showSkillsDetails && !isLoading && <div className="archetypes-background" style={{ backgroundImage: `url(/CountriesMap/${countryCode}.png)` }}></div>}
        {!selectedParentISCO ?
            <>
                {isLoading && <img src={LoaderImage} alt="" className="loadingImg" style={{ width: 60 }} />}
                <div id={chartId} className="parent-isco-chart-container"></div>
            </>
            :
            <ChildISCOChart handleGoBackToParentISCOChart={handleGoBackToParentISCOChart} countryCode={countryCode} />
        }
    </>;
};

export default ParentISCOChart;
